'use client';

import { useState, useEffect } from 'react';
import {
  X,
  Circle,
  Signal,
  Users,
  Tags,
  CalendarDays,
  CalendarCheck,
  Target,
  Box,
  GitFork,
  ChevronDown,
  Check,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useProject } from '@/contexts/project-context';
import { useSpace } from '@/contexts/space-context';
import { getIconComponent } from '@/components/icon-picker';
import { API } from '@pilot/api';

interface CreateIssueDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

interface Member {
  id: string;
  user: {
    id: string;
    fullName: string;
    email: string;
  };
  role: string;
}

const STATUS_OPTIONS = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent', color: 'text-red-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'low', label: 'Low', color: 'text-blue-500' },
  { value: 'none', label: 'None', color: 'text-muted-foreground' },
];

export function CreateIssueDialog({ isOpen, onClose, onCreated }: CreateIssueDialogProps) {
  const { selectedProject } = useProject();
  const { space } = useSpace();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('backlog');
  const [priority, setPriority] = useState('none');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [members, setMembers] = useState<Member[]>([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  useEffect(() => {
    if (isOpen && selectedProject) {
      fetchMembers();
    }
  }, [isOpen, selectedProject]);

  const fetchMembers = async () => {
    if (!selectedProject) return;
    try {
      const data = await API.projects.getMembers(space.slug, selectedProject.code);
      setMembers(data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !selectedProject) return;

    setError('');
    setLoading(true);

    try {
      await API.issues.create(space.slug, selectedProject.code, {
        title,
        description,
        status,
        priority,
        assignees,
        startDate: startDate || undefined,
        dueDate: dueDate || undefined,
      });

      onCreated?.();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setStatus('backlog');
    setPriority('none');
    setAssignees([]);
    setStartDate('');
    setDueDate('');
    setError('');
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const toggleAssignee = (userId: string) => {
    setAssignees((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredMembers = members.filter((m) =>
    m.user.fullName.toLowerCase().includes(assigneeSearch.toLowerCase()) ||
    m.user.email.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const getInitials = (name: string) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const selectedStatus = STATUS_OPTIONS.find((s) => s.value === status);
  const selectedPriority = PRIORITY_OPTIONS.find((p) => p.value === priority);

  const ProjectIcon = selectedProject ? getIconComponent(selectedProject.icon || 'folder') : null;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-background border rounded-lg shadow-xl z-50 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Create new issue</h2>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-visible p-4 space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-500 bg-red-50 rounded-md">
              {error}
            </div>
          )}

          {/* Project Badge */}
          {selectedProject && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-2 py-1 bg-muted rounded text-sm">
                {ProjectIcon && <ProjectIcon className="h-4 w-4" />}
                <span>{selectedProject.name}</span>
              </div>
            </div>
          )}

          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full px-3 py-2 border rounded-md bg-background text-base focus:outline-none focus:ring-2 focus:ring-primary"
            autoFocus
          />

          {/* Description */}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Click to add description"
            rows={4}
            className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />

          {/* Property Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Status */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted"
              >
                <Circle className="h-3.5 w-3.5" />
                {selectedStatus?.label}
                <ChevronDown className="h-3 w-3" />
              </button>
              {showStatusDropdown && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setShowStatusDropdown(false)} />
                  <div className="absolute top-full left-0 mt-1 w-40 bg-popover border rounded-md shadow-lg z-[70] py-1">
                    {STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setStatus(opt.value); setShowStatusDropdown(false); }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted text-left"
                      >
                        {status === opt.value && <Check className="h-3 w-3" />}
                        <span className={status === opt.value ? 'font-medium' : ''}>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Priority */}
            <div className="relative">
              <button
                onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                className={`flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted ${selectedPriority?.color}`}
              >
                <Signal className="h-3.5 w-3.5" />
                {selectedPriority?.label}
                <ChevronDown className="h-3 w-3" />
              </button>
              {showPriorityDropdown && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setShowPriorityDropdown(false)} />
                  <div className="absolute top-full left-0 mt-1 w-36 bg-popover border rounded-md shadow-lg z-[70] py-1">
                    {PRIORITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => { setPriority(opt.value); setShowPriorityDropdown(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted text-left ${opt.color}`}
                      >
                        {priority === opt.value && <Check className="h-3 w-3" />}
                        <span className={priority === opt.value ? 'font-medium' : ''}>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Assignees */}
            <div className="relative">
              <button
                onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted"
              >
                <Users className="h-3.5 w-3.5" />
                {assignees.length > 0 ? `${assignees.length} Assignee${assignees.length > 1 ? 's' : ''}` : 'Assignees'}
                <ChevronDown className="h-3 w-3" />
              </button>
              {showAssigneeDropdown && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setShowAssigneeDropdown(false)} />
                  <div className="absolute top-full left-0 mt-1 w-64 bg-popover border rounded-md shadow-lg z-[70]">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          type="text"
                          value={assigneeSearch}
                          onChange={(e) => setAssigneeSearch(e.target.value)}
                          placeholder="Search members..."
                          className="w-full pl-8 pr-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {filteredMembers.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">No members found</div>
                      ) : (
                        filteredMembers.map((member) => (
                          <button
                            key={member.user.id}
                            onClick={() => toggleAssignee(member.user.id)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted text-left"
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              {assignees.includes(member.user.id) && <Check className="h-3 w-3" />}
                            </div>
                            <Avatar className="h-5 w-5">
                              <AvatarFallback className="text-[10px] bg-primary/10">
                                {getInitials(member.user.fullName)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate">{member.user.fullName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Labels */}
            <button className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted text-muted-foreground">
              <Tags className="h-3.5 w-3.5" />
              Labels
            </button>

            {/* Start Date */}
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted cursor-pointer"
              />
            </div>

            {/* Due Date */}
            <div className="relative">
              <input
                type="date"
                value={dueDate}
                min={startDate || undefined}
                onChange={(e) => setDueDate(e.target.value)}
                className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted cursor-pointer"
              />
            </div>

            {/* Milestone */}
            <button className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted text-muted-foreground">
              <Target className="h-3.5 w-3.5" />
              Milestone
            </button>

            {/* Components */}
            <button className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted text-muted-foreground">
              <Box className="h-3.5 w-3.5" />
              Components
            </button>

            {/* Parent */}
            <button className="flex items-center gap-1.5 px-2 py-1 border rounded-md text-sm hover:bg-muted text-muted-foreground">
              <GitFork className="h-3.5 w-3.5" />
              Add parent
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <Button variant="ghost" onClick={handleClose}>
            Discard
          </Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  );
}
